import { useCallback, useEffect, useRef, useState } from 'react'
import { toPageMeta } from './to-page-meta'
import { sdkErrorMessage, useAuthServiceClient } from './sdk-client'

type Client = Awaited<ReturnType<ReturnType<typeof useAuthServiceClient>>>
type Params = NonNullable<Parameters<Client['auth']['getAccountAuditLog']>[1]>
type Entry = NonNullable<Awaited<ReturnType<Client['auth']['getAccountAuditLog']>>['data']>[number]

export type ChangeSetEntry = Entry

export interface ChangeSetEntriesArgs {
  changeSetId: string
  // null = an account-level row, requested with the server's 'none' sentinel.
  permissionSetKey: string | null
  resourceType?: NonNullable<Params['resourceType_']>
  since?: string
}

export interface ChangeSetEntriesMeta {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
}

export type ChangeSetEntriesState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'ready'
      data: Entry[]
      meta: ChangeSetEntriesMeta
      loadingMore: boolean
      loadError?: string
      loadMore: () => void
    }

const PER_PAGE = 100

export function useChangeSetEntries(slug: string, args: ChangeSetEntriesArgs): ChangeSetEntriesState {
  const getClient = useAuthServiceClient()
  // Callers build `args` inline, so depend on its value, not its identity.
  const argsKey = JSON.stringify(args)
  // State is tagged with the request it answers, so a slug or change-set switch never shows the old rows.
  const key = `${slug}|${argsKey}`
  const [tagged, setTagged] = useState<{ key?: string; state: ChangeSetEntriesState }>({ state: { status: 'loading' } })
  const setState = useCallback((update: (prev: ChangeSetEntriesState) => ChangeSetEntriesState) => {
    setTagged((t) => ({ key: t.key, state: update(t.state) }))
  }, [])
  // Bumped per fetch cycle; a response from an older cycle is dropped.
  const generation = useRef(0)
  const busy = useRef(false)
  const next = useRef<{ page: number; totalPages: number }>({ page: 1, totalPages: 1 })
  const request = useRef<(page: number) => Promise<{ rows: Entry[]; meta: ChangeSetEntriesMeta }>>(undefined)

  const loadMore = useCallback(() => {
    const fetchPage = request.current
    if (busy.current || !fetchPage || next.current.page >= next.current.totalPages) return
    const mine = generation.current
    busy.current = true
    setState((prev) => (prev.status === 'ready' ? { ...prev, loadingMore: true, loadError: undefined } : prev))

    fetchPage(next.current.page + 1)
      .then(({ rows, meta }) => {
        if (mine !== generation.current) return
        next.current = { page: meta.page, totalPages: meta.totalPages }
        setState((prev) => {
          if (prev.status !== 'ready') return prev
          // Guard: the server order is stable, but a repeat must never double-render.
          const seen = new Set(prev.data.map((e) => e.id))
          return { ...prev, data: [...prev.data, ...rows.filter((e) => !seen.has(e.id))], meta, loadingMore: false }
        })
      })
      .catch((error: unknown) => {
        if (mine !== generation.current) return
        const loadError = sdkErrorMessage(error, 'Failed to load more changes.')
        setState((prev) => (prev.status === 'ready' ? { ...prev, loadingMore: false, loadError } : prev))
      })
      .finally(() => {
        if (mine === generation.current) busy.current = false
      })
  }, [setState])

  useEffect(() => {
    const a = JSON.parse(argsKey) as ChangeSetEntriesArgs
    const mine = ++generation.current
    busy.current = false
    next.current = { page: 1, totalPages: 1 }
    setTagged({ key, state: { status: 'loading' } })

    request.current = async (page) => {
      const client = await getClient()
      const response = await client.auth.getAccountAuditLog(slug, {
        changeSetId: a.changeSetId,
        permissionSetKey: a.permissionSetKey ?? 'none',
        resourceType_: a.resourceType?.length ? a.resourceType : undefined,
        since: a.since,
        page,
        perPage: PER_PAGE,
      })
      const rows = response.data ?? []
      return { rows, meta: toPageMeta(response.meta, page, PER_PAGE, rows.length) }
    }

    request.current(1)
      .then(({ rows, meta }) => {
        if (mine !== generation.current) return
        next.current = { page: meta.page, totalPages: meta.totalPages }
        setTagged({ key, state: { status: 'ready', data: rows, meta, loadingMore: false, loadMore } })
      })
      .catch((error: unknown) => {
        if (mine === generation.current) setTagged({ key, state: { status: 'error', message: sdkErrorMessage(error, 'Failed to load changes.') } })
      })
  }, [getClient, slug, argsKey, key, loadMore])

  return tagged.key === key ? tagged.state : { status: 'loading' }
}
